import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "bg-white rounded-lg"
}
const _hoisted_2 = { class: "flex items-center justify-between mb-2" }
const _hoisted_3 = { class: "text-xs text-black" }
const _hoisted_4 = {
  key: 0,
  class: "text-xs text-red-600 mb-2"
}
const _hoisted_5 = {
  key: 1,
  class: "text-xs text-red-600 mb-2"
}
const _hoisted_6 = {
  key: 2,
  class: "space-y-2"
}
const _hoisted_7 = {
  key: 0,
  class: "space-y-1"
}
const _hoisted_8 = { class: "flex flex-wrap gap-2" }
const _hoisted_9 = ["title", "onClick"]
const _hoisted_10 = { class: "flex gap-2" }
const _hoisted_11 = {
  key: 0,
  value: "0-Produit"
}
const _hoisted_12 = ["value"]
const _hoisted_13 = { class: "flex-1 relative" }
const _hoisted_14 = ["onKeydown"]
const _hoisted_15 = {
  key: 0,
  class: "px-3 py-1.5 text-[11px] uppercase tracking-wide text-gray-500 bg-gray-50 border-b border-gray-100"
}
const _hoisted_16 = {
  key: 1,
  class: "px-3 py-1.5 text-[11px] uppercase tracking-wide text-gray-500 bg-gray-50 border-b border-gray-100"
}
const _hoisted_17 = ["onMousedown"]
const _hoisted_18 = {
  key: 0,
  class: "fas fa-history shrink-0 text-[10px] text-gray-400",
  title: "Composition récente"
}
const _hoisted_19 = {
  key: 0,
  class: "absolute right-2 top-2"
}
const _hoisted_20 = {
  key: 1,
  class: "space-y-2"
}
const _hoisted_21 = { class: "text-sm font-medium text-black mb-1" }
const _hoisted_22 = { class: "space-y-2" }
const _hoisted_23 = { class: "flex-1 px-3 py-2 bg-gray-100 rounded text-sm text-black" }
const _hoisted_24 = { class: "relative" }
const _hoisted_25 = ["data-composition-percentage", "value", "onChange", "onKeydown"]
const _hoisted_26 = ["onClick"]
const _hoisted_27 = {
  key: 0,
  class: "text-sm text-red-600"
}
const _hoisted_28 = {
  key: 1,
  class: "text-sm text-green-600"
}
const _hoisted_29 = {
  key: 3,
  class: "space-y-2"
}
const _hoisted_30 = { class: "grid grid-cols-1 md:grid-cols-3 gap-3" }
const _hoisted_31 = { class: "text-sm font-medium text-black mb-2 text-center" }
const _hoisted_32 = { class: "space-y-2" }
const _hoisted_33 = ["checked", "onChange"]
const _hoisted_34 = { class: "text-sm text-black" }

import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useNotification } from "@/composables/useNotification";
import type {
  CompositionItem as CompositionData,
  CompositionLocalisation,
  CompositionMateriau,
  CompositionAutocompleteOption,
  CompositionType,
} from "@/types/mel-components";
import {
  filterRecentCompositions,
  getRecentCompositionSets,
  rememberComposition,
  type RecentCompositionSet,
} from "@/utils/melCompositionHistory";

interface Props {
  modelValue: CompositionData[];
  compositionType: CompositionType;
  localisations: CompositionLocalisation[];
  compositionsList: CompositionMateriau[];
  autocompleteOptions: CompositionAutocompleteOption[];
  showAutocompleteDropdown: boolean;
  isLoadingAutocomplete: boolean;
}

interface Emits {
  (e: "update:modelValue", value: CompositionData[]): void;
  (e: "composition-input", value: string, localisationId: string | null): void;
  (e: "hide-dropdown"): void;
  (e: "select-autocomplete", value: CompositionAutocompleteOption): void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CompositionForm',
  props: {
    modelValue: {},
    compositionType: {},
    localisations: {},
    compositionsList: {},
    autocompleteOptions: {},
    showAutocompleteDropdown: { type: Boolean },
    isLoadingAutocomplete: { type: Boolean }
  },
  emits: ["update:modelValue", "composition-input", "hide-dropdown", "select-autocomplete"],
  setup(__props: any, { emit: __emit }) {

const { error } = useNotification();

const props = __props;
const emit = __emit;

// Vérifier si "Produit" existe déjà dans les localisations de la DB
const hasProduitInLocalisations = computed(() => {
  return props.localisations.some(
    (loc) => loc.libelle?.toLowerCase() === "produit",
  );
});

// Trouver la localisation "Produit" de la DB
const produitLocalisation = computed(() => {
  return props.localisations.find(
    (loc) => loc.libelle?.toLowerCase() === "produit",
  );
});

// Valeur par défaut : utiliser la localisation "Produit" de la DB si elle existe, sinon "0-Produit"
const defaultLocalisationValue = computed(() => {
  if (produitLocalisation.value) {
    return `${produitLocalisation.value.id_composition_localisation}-${produitLocalisation.value.libelle}`;
  }
  return "0-Produit";
});

const selectedLocalisation = ref("");
const newCompositionText = ref("");
const selectedCompositionId = ref<number>(0);
const highlightedIndex = ref(-1);
const isInputFocused = ref(false);
const suppressDropdownAfterSelection = ref(false);
const compositionInputRef = ref<HTMLInputElement | null>(null);
const recentCompositionSets = ref<RecentCompositionSet[]>(
  getRecentCompositionSets(),
);
const dropdownStyle = ref({
  top: "0px",
  left: "0px",
  width: "0px",
});

const selectedLocalisationId = computed(() => {
  if (!selectedLocalisation.value) return null;
  const id = Number.parseInt(selectedLocalisation.value.split("-")[0], 10);
  return Number.isFinite(id) ? id : null;
});

const excludedCompositionIds = computed(() =>
  props.modelValue
    .filter((comp) =>
      selectedLocalisationId.value == null
        ? true
        : comp.localisationId === selectedLocalisationId.value,
    )
    .map((comp) => comp.compositionId)
    .filter((id) => id > 0),
);

const displayOptions = computed(() => {
  const recent = filterRecentCompositions(
    newCompositionText.value,
    excludedCompositionIds.value,
  ).map((option) => ({ ...option, fromRecent: true as const }));
  const recentIds = new Set(recent.map((option) => option.id));
  const suggestions = props.autocompleteOptions
    .filter((option) => !recentIds.has(option.id))
    .filter((option) => !excludedCompositionIds.value.includes(option.id))
    .map((option) => ({ ...option, fromRecent: false as const }));
  return [...recent, ...suggestions];
});

const showDropdown = computed(
  () =>
    isInputFocused.value &&
    !suppressDropdownAfterSelection.value &&
    displayOptions.value.length > 0 &&
    (props.showAutocompleteDropdown ||
      displayOptions.value.some((option) => option.fromRecent)),
);

function updateDropdownPosition() {
  const element = compositionInputRef.value;
  if (!element) return;
  const rect = element.getBoundingClientRect();
  dropdownStyle.value = {
    top: `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
  };
}

function handleDropdownReposition() {
  if (!showDropdown.value) {
    return;
  }
  updateDropdownPosition();
}

if (typeof window !== "undefined") {
  window.addEventListener("resize", handleDropdownReposition);
  window.addEventListener("scroll", handleDropdownReposition, true);
}

onBeforeUnmount(() => {
  if (typeof window === "undefined") return;
  window.removeEventListener("resize", handleDropdownReposition);
  window.removeEventListener("scroll", handleDropdownReposition, true);
});

const groupedCompositions = computed(() => {
  const groups: Record<number, CompositionData[]> = {};
  props.modelValue.forEach((comp) => {
    if (!groups[comp.localisationId]) {
      groups[comp.localisationId] = [];
    }
    groups[comp.localisationId].push(comp);
  });
  return groups;
});

const isPercentagesValid = computed(() => {
  if (props.compositionType !== "autres") return true;

  const localisationGroups: { [key: number]: number } = {};

  // Règles:
  // - Chaque pourcentage doit être strictement > 0
  // - La somme par localisation doit être exactement 100
  for (const comp of props.modelValue) {
    const percentage = Number(comp.percentage) || 0;
    // Interdire 0% ou valeurs négatives
    if (percentage <= 0) {
      return false;
    }
    if (localisationGroups[comp.localisationId]) {
      localisationGroups[comp.localisationId] += percentage;
    } else {
      localisationGroups[comp.localisationId] = percentage;
    }
  }

  for (const localisationId in localisationGroups) {
    if (Math.abs(localisationGroups[localisationId] - 100) > 0.01) {
      return false;
    }
  }

  return true;
});

const isChaussuresValid = computed(() => {
  if (props.compositionType !== "chaussures") return true;

  const localisationIds = props.localisations.map(
    (loc) => loc.id_composition_localisation,
  );
  return localisationIds.every((localisationId) =>
    props.modelValue.some((comp) => comp.localisationId === localisationId),
  );
});

const getLocalisationName = (localisationId: number): string => {
  if (localisationId === 0) {
    return "Produit";
  }
  const localisation = props.localisations.find(
    (loc) => loc.id_composition_localisation === localisationId,
  );
  return localisation ? localisation.libelle : `Localisation ${localisationId}`;
};

// Retourne le libellé affichable d'une composition
const getCompositionName = (comp: CompositionData): string => {
  // Priorité au nom déjà présent dans l'élément (souvent saisi via l'autocomplete)
  if (comp.compositionName && comp.compositionName.trim() !== "") {
    return comp.compositionName;
  }
  // Sinon, on tente de retrouver le libellé via la liste des compositions disponibles
  const compId = Number(comp.compositionId);
  const found = props.compositionsList.find(
    (c) => Number(c.id_composition) === compId,
  );
  return found ? found.libelle : `Composition ${comp.compositionId}`;
};

const handleInput = () => {
  suppressDropdownAfterSelection.value = false;
  selectedCompositionId.value = 0;
  highlightedIndex.value = -1;
  updateDropdownPosition();
  // Extraire l'ID de la localisation sélectionnée
  const localisationId = selectedLocalisation.value
    ? selectedLocalisation.value.split("-")[0]
    : null;
  emit("composition-input", newCompositionText.value, localisationId);
};

const handleFocus = () => {
  isInputFocused.value = true;
  suppressDropdownAfterSelection.value = false;
  handleInput();
};

const handleBlur = () => {
  isInputFocused.value = false;
  emit("hide-dropdown");
};

watch(
  () => [showDropdown.value, displayOptions.value.length],
  () => {
    if (!showDropdown.value) {
      return;
    }
    void nextTick(updateDropdownPosition);
  }
);

const handleArrowDown = () => {
  if (showDropdown.value && displayOptions.value.length > 0) {
    highlightedIndex.value = Math.min(
      highlightedIndex.value + 1,
      displayOptions.value.length - 1,
    );
  }
};

const handleArrowUp = () => {
  if (showDropdown.value && displayOptions.value.length > 0) {
    highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0);
  }
};

const handleEnter = () => {
  if (showDropdown.value && displayOptions.value.length) {
    const index = highlightedIndex.value >= 0 ? highlightedIndex.value : 0;
    selectFromAutocomplete(displayOptions.value[index]);
  } else {
    addComposition();
  }
};

const hideAutocomplete = () => {
  highlightedIndex.value = -1;
  emit("hide-dropdown");
};

const selectFromAutocomplete = (composition: CompositionAutocompleteOption) => {
  // Renseigner l'entrée et l'ID sélectionné
  newCompositionText.value = composition.label;
  selectedCompositionId.value = composition.id;
  emit("select-autocomplete", composition);
  // Si aucune localisation choisie, utiliser la valeur par défaut (Produit)
  if (!selectedLocalisation.value) {
    selectedLocalisation.value = defaultLocalisationValue.value;
  }
  // Ajouter automatiquement dès la sélection
  addComposition();
  // Fermer le dropdown
  emit("hide-dropdown");
};

const addComposition = () => {
  if (!selectedLocalisation.value || !newCompositionText.value.trim()) return;

  const [localisationId] = selectedLocalisation.value.split("-");
  const compositionName = newCompositionText.value.trim();

  // ⚠️ VALIDATION : La composition DOIT exister dans la DB (autocomplete)
  if (selectedCompositionId.value === 0) {
    error(
      "⚠️ Veuillez sélectionner une composition valide depuis la liste. Les compositions personnalisées ne sont pas autorisées.",
    );
    return;
  }

  const exists = props.modelValue.some(
    (comp) =>
      comp.localisationId === parseInt(localisationId) &&
      comp.compositionId === selectedCompositionId.value &&
      comp.compositionName === compositionName,
  );

  if (exists) {
    error("Cette composition existe déjà pour cette localisation.");
    return;
  }

  const hasCompositionAtLocation = props.modelValue.some(
    (comp) => comp.localisationId === parseInt(localisationId),
  );
  rememberComposition({
    id: selectedCompositionId.value,
    label: compositionName,
  });
  const updatedCompositions: CompositionData[] = [
    ...props.modelValue,
    {
      localisationId: parseInt(localisationId),
      compositionId: selectedCompositionId.value,
      compositionName: compositionName,
      percentage: hasCompositionAtLocation ? "" : 100,
    },
  ];

  emit("update:modelValue", updatedCompositions);
  newCompositionText.value = "";
  selectedCompositionId.value = 0;
  highlightedIndex.value = -1;
  suppressDropdownAfterSelection.value = true;
  emit("hide-dropdown");
  void nextTick(() => {
    const selector = `[data-composition-percentage="${localisationId}-${updatedCompositions[updatedCompositions.length - 1].compositionId}"]`;
    const percentageInput = document.querySelector<HTMLInputElement>(selector);
    percentageInput?.focus();
    percentageInput?.select();
  });
};

const applyRecentCompositionSet = (set: RecentCompositionSet) => {
  const localisationId = selectedLocalisationId.value ?? 0;
  const otherLocations = props.modelValue.filter(
    (composition) => composition.localisationId !== localisationId,
  );
  emit("update:modelValue", [
    ...otherLocations,
    ...set.items.map((item) => ({ ...item, localisationId })),
  ]);
  emit("hide-dropdown");
};

const removeComposition = (composition: CompositionData) => {
  const updatedCompositions = props.modelValue.filter(
    (comp) =>
      !(
        comp.localisationId === composition.localisationId &&
        comp.compositionId === composition.compositionId &&
        comp.compositionName === composition.compositionName
      ),
  );
  const remainingAtLocation = updatedCompositions.filter(
    (item) => item.localisationId === composition.localisationId,
  );
  if (remainingAtLocation.length === 1) {
    remainingAtLocation[0].percentage = 100;
  }
  emit("update:modelValue", updatedCompositions);
};

const updatePercentage = (comp: CompositionData, event: Event) => {
  const target = event.target as HTMLInputElement;
  // Accepter virgule et point comme séparateur décimal
  const value = target.value.replace(',', '.');

  // Convertir en nombre, en gardant la valeur exacte saisie
  let numValue: number | "" = "";
  if (value !== "") {
    const parsed = parseFloat(value);
    if (!isNaN(parsed)) {
      const rounded = Math.round(parsed * 100) / 100;
      numValue = Math.max(0.01, Math.min(100, rounded));
    }
  }

  // Mettre à jour la composition
  comp.percentage = numValue;

  // Émettre la mise à jour
  emit("update:modelValue", [...props.modelValue]);
};

const finishPercentage = (comp: CompositionData, event: Event) => {
  updatePercentage(comp, event);
  void nextTick(() => compositionInputRef.value?.focus());
};

const isCompositionSelected = (
  localisationId: number,
  compositionId: number,
): boolean => {
  return props.modelValue.some(
    (comp) =>
      comp.localisationId === localisationId &&
      comp.compositionId === compositionId,
  );
};

const toggleChaussureComposition = (
  localisationId: number,
  compositionId: number,
) => {
  const existingIndex = props.modelValue.findIndex(
    (comp) =>
      comp.localisationId === localisationId &&
      comp.compositionId === compositionId,
  );

  if (existingIndex > -1) {
    // Retirer la composition
    const updatedCompositions = [...props.modelValue];
    updatedCompositions.splice(existingIndex, 1);
    emit("update:modelValue", updatedCompositions);
  } else {
    // Ajouter la composition
    const composition = props.compositionsList.find(
      (comp) => comp.id_composition === compositionId,
    );
    if (composition) {
      const updatedCompositions = [
        ...props.modelValue,
        {
          localisationId: localisationId,
          compositionId: compositionId,
          compositionName: composition.libelle,
          percentage: "",
        },
      ];
      emit("update:modelValue", updatedCompositions);
    }
  }
};

// Watcher pour maintenir "Produit" comme valeur par défaut
watch(
  () => [props.compositionType, defaultLocalisationValue.value],
  ([newType, defaultValue]) => {
    if (newType === "autres") {
      if (!selectedLocalisation.value || selectedLocalisation.value === "") {
        selectedLocalisation.value = defaultValue;
      }
    }
  },
  { immediate: true },
);

// Watcher pour initialiser la valeur par défaut quand les localisations sont chargées
watch(
  () => props.localisations,
  (newLocalisations) => {
    if (
      newLocalisations &&
      newLocalisations.length > 0 &&
      props.compositionType === "autres"
    ) {
      if (!selectedLocalisation.value || selectedLocalisation.value === "") {
        selectedLocalisation.value = defaultLocalisationValue.value;
      }
    }
  },
  { immediate: true },
);

// Watcher pour réinitialiser à "Produit" si la valeur est vide
watch(selectedLocalisation, (newValue) => {
  if (props.compositionType === "autres" && (!newValue || newValue === "")) {
    selectedLocalisation.value = defaultLocalisationValue.value;
  }
});


return (_ctx: any,_cache: any) => {
  return (_ctx.compositionType)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _cache[2] || (_cache[2] = _createElementVNode("h4", { class: "text-base font-semibold text-black" }, [
            _createTextVNode(" Composition "),
            _createElementVNode("span", { class: "text-red-500" }, "*")
          ], -1)),
          _createElementVNode("div", _hoisted_3, " Type: " + _toDisplayString(_ctx.compositionType === "autres" ? "Autres" : "Chaussures"), 1)
        ]),
        (_ctx.compositionType && _ctx.modelValue.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, " ⚠️ Les compositions sont obligatoires. "))
          : _createCommentVNode("", true),
        (_ctx.compositionType === 'chaussures' && !isChaussuresValid.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, " ⚠️ Sélectionnez au moins un matériau pour chaque zone : Tige, Doublure et Semelle. "))
          : _createCommentVNode("", true),
        (_ctx.compositionType === 'autres')
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              (recentCompositionSets.value.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                    _cache[3] || (_cache[3] = _createElementVNode("p", { class: "text-xs font-medium text-gray-600" }, "Compositions récentes", -1)),
                    _createElementVNode("div", _hoisted_8, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(recentCompositionSets.value, (set) => {
                        return (_openBlock(), _createElementBlock("button", {
                          key: set.label,
                          type: "button",
                          class: "px-3 py-1.5 text-xs text-left text-blue-800 bg-blue-50 border border-blue-200 rounded-full hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500",
                          title: `Réutiliser : ${set.label}`,
                          onClick: ($event: any) => (applyRecentCompositionSet(set))
                        }, _toDisplayString(set.label), 9, _hoisted_9))
                      }), 128))
                    ])
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_10, [
                _withDirectives(_createElementVNode("select", {
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedLocalisation).value = $event)),
                  class: "flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm text-black"
                }, [
                  _cache[4] || (_cache[4] = _createElementVNode("option", { value: "" }, "Sélectionner ...", -1)),
                  (!hasProduitInLocalisations.value)
                    ? (_openBlock(), _createElementBlock("option", _hoisted_11, " Produit "))
                    : _createCommentVNode("", true),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.localisations, (localisation) => {
                    return (_openBlock(), _createElementBlock("option", {
                      key: localisation.id_composition_localisation,
                      value: `${localisation.id_composition_localisation}-${localisation.libelle}`
                    }, _toDisplayString(localisation.libelle), 9, _hoisted_12))
                  }), 128))
                ], 512), [
                  [_vModelSelect, selectedLocalisation.value]
                ]),
                _createElementVNode("div", _hoisted_13, [
                  _withDirectives(_createElementVNode("input", {
                    ref_key: "compositionInputRef",
                    ref: compositionInputRef,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((newCompositionText).value = $event)),
                    type: "text",
                    placeholder: "-- Tapez une composition --",
                    class: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm text-black",
                    onInput: handleInput,
                    onKeydown: [
                      _withKeys(_withModifiers(handleArrowDown, ["prevent"]), ["down"]),
                      _withKeys(_withModifiers(handleArrowUp, ["prevent"]), ["up"]),
                      _withKeys(_withModifiers(handleEnter, ["prevent"]), ["enter"]),
                      _withKeys(_withModifiers(hideAutocomplete, ["prevent"]), ["esc"])
                    ],
                    onBlur: handleBlur,
                    onFocus: handleFocus
                  }, null, 40, _hoisted_14), [
                    [_vModelText, newCompositionText.value]
                  ]),
                  (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
                    (showDropdown.value)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 0,
                          class: "fixed z-[10050] bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto",
                          style: _normalizeStyle(dropdownStyle.value)
                        }, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayOptions.value, (option, index) => {
                            return (_openBlock(), _createElementBlock(_Fragment, {
                              key: `${option.id}-${option.fromRecent ? 'recent' : 'suggestion'}`
                            }, [
                              (index === 0 && option.fromRecent)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_15, " Récemment utilisées "))
                                : (
                    !option.fromRecent &&
                    (index === 0 || displayOptions.value[index - 1]?.fromRecent)
                  )
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_16, " Suggestions "))
                                  : _createCommentVNode("", true),
                              _createElementVNode("div", {
                                onMousedown: _withModifiers(($event: any) => (selectFromAutocomplete(option)), ["prevent"]),
                                class: _normalizeClass([
                    'px-3 py-2 cursor-pointer text-sm border-b border-gray-100 last:border-b-0 flex items-center justify-between gap-2',
                    index === highlightedIndex.value
                      ? 'bg-blue-100'
                      : 'hover:bg-gray-100',
                  ])
                              }, [
                                _createElementVNode("span", null, _toDisplayString(option.label), 1),
                                (option.fromRecent)
                                  ? (_openBlock(), _createElementBlock("i", _hoisted_18))
                                  : _createCommentVNode("", true)
                              ], 42, _hoisted_17)
                            ], 64))
                          }), 128))
                        ], 4))
                      : _createCommentVNode("", true)
                  ])),
                  (_ctx.isLoadingAutocomplete)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_19, _cache[5] || (_cache[5] = [
                        _createElementVNode("i", { class: "fas fa-spinner fa-spin text-black" }, null, -1)
                      ])))
                    : _createCommentVNode("", true)
                ])
              ]),
              (_ctx.modelValue.length > 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupedCompositions.value, (group, localisationId) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: localisationId,
                        class: "border border-gray-300 rounded-lg p-2 bg-white"
                      }, [
                        _createElementVNode("h5", _hoisted_21, _toDisplayString(getLocalisationName(parseInt(localisationId))), 1),
                        _createElementVNode("div", _hoisted_22, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group, (comp) => {
                            return (_openBlock(), _createElementBlock("div", {
                              key: `${comp.localisationId}-${comp.compositionId}-${
                comp.compositionName || ''
              }`,
                              class: "flex items-center gap-2"
                            }, [
                              _createElementVNode("span", _hoisted_23, _toDisplayString(getCompositionName(comp)), 1),
                              _createElementVNode("div", _hoisted_24, [
                                _createElementVNode("input", {
                                  "data-composition-percentage": `${comp.localisationId}-${comp.compositionId}`,
                                  value: comp.percentage !== '' && comp.percentage != null ? +Number(comp.percentage).toFixed(2) : comp.percentage,
                                  type: "text",
                                  inputmode: "decimal",
                                  placeholder: "0",
                                  class: "peer w-20 px-2 py-1 pr-6 border border-gray-300 focus:border-blue-600 focus:outline-none rounded text-sm text-center text-black",
                                  onChange: (e) => updatePercentage(comp, e),
                                  onKeydown: _withKeys(_withModifiers((e) => finishPercentage(comp, e), ["prevent"]), ["enter"])
                                }, null, 40, _hoisted_25),
                                _cache[6] || (_cache[6] = _createElementVNode("span", { class: "absolute right-2 top-1/2 transform -translate-y-1/2 text-black peer-focus:text-black text-sm pointer-events-none" }, "%", -1))
                              ]),
                              _createElementVNode("button", {
                                type: "button",
                                onClick: ($event: any) => (removeComposition(comp)),
                                class: "px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                              }, _cache[7] || (_cache[7] = [
                                _createElementVNode("i", { class: "fas fa-trash" }, null, -1)
                              ]), 8, _hoisted_26)
                            ]))
                          }), 128))
                        ])
                      ]))
                    }), 128)),
                    (!isPercentagesValid.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_27, " ⚠️ Chaque partie doit totaliser exactement 100% et aucun élément ne peut être à 0% "))
                      : (isPercentagesValid.value && _ctx.modelValue.length > 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_28, " ✓ Composition valide "))
                        : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true)
            ]))
          : (_ctx.compositionType === 'chaussures')
            ? (_openBlock(), _createElementBlock("div", _hoisted_29, [
                _cache[8] || (_cache[8] = _createElementVNode("div", { class: "text-xs text-black mb-2" }, " Sélectionnez les compositions pour chaque partie : ", -1)),
                _createElementVNode("div", _hoisted_30, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.localisations, (localisation) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: localisation.id_composition_localisation,
                      class: "border border-gray-300 rounded-lg p-2 bg-gray-50"
                    }, [
                      _createElementVNode("h5", _hoisted_31, _toDisplayString(localisation.libelle), 1),
                      _createElementVNode("div", _hoisted_32, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.compositionsList, (composition) => {
                          return (_openBlock(), _createElementBlock("label", {
                            key: composition.id_composition,
                            class: "flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                          }, [
                            _createElementVNode("input", {
                              type: "checkbox",
                              checked: 
                  isCompositionSelected(
                    localisation.id_composition_localisation,
                    composition.id_composition,
                  )
                ,
                              onChange: ($event: any) => (
                  toggleChaussureComposition(
                    localisation.id_composition_localisation,
                    composition.id_composition,
                  )
                ),
                              class: "rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            }, null, 40, _hoisted_33),
                            _createElementVNode("span", _hoisted_34, _toDisplayString(composition.libelle), 1)
                          ]))
                        }), 128))
                      ])
                    ]))
                  }), 128))
                ])
              ]))
            : _createCommentVNode("", true)
      ]))
    : _createCommentVNode("", true)
}
}

})